/**
 * Bank Activity — live Akahu transactions inside The Study.
 *
 * Calm rows: Date · Merchant · Account · Category ▾ · For ▾ · Amount.
 * Category changes save immediately through the existing endpoint and feed
 * the existing category-learning service. No modals, no dashboards.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Landmark } from "lucide-react";
import api, { parseResponse } from "../../../lib/api";
import { useAuth } from "../../../App";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { PillButton, TextLink } from "./os/editorial";

const inter = { fontFamily: "Inter, system-ui, sans-serif" };

/* Quiet metadata tag with a dropdown — used for Category ▾ and For ▾. */
export function TagMenu({ label, placeholder, groups, onSelect, testid, align = "left" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 min-h-[28px] transition-ara hover:opacity-70"
        style={{
          ...inter, fontWeight: 450, fontSize: 12,
          border: "1px solid var(--ara-line)",
          color: label ? "var(--ara-ink)" : "var(--ara-ink-soft)",
          background: "transparent",
        }}
      >
        <span className="truncate max-w-[150px]">{label || placeholder}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--ara-ink-soft)" }} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} aria-hidden />
          <div
            className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-1 w-[230px] max-h-[300px] overflow-y-auto z-[91] rounded-[12px] py-1`}
            style={{ background: "var(--ara-surface)", border: "1px solid var(--ara-line)", boxShadow: "0 8px 24px rgba(31,31,31,0.06)" }}
            data-testid={testid ? `${testid}-menu` : undefined}
          >
            {groups.map((g, gi) => (
              <div key={g.label || gi}>
                {g.label && (
                  <p className="px-3 pt-2.5 pb-1 text-[10px] tracking-[0.12em] uppercase" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 500 }}>
                    {g.label}
                  </p>
                )}
                {g.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { setOpen(false); onSelect(item); }}
                    className="w-full text-left px-3 py-2 min-h-[36px] text-[13px] hover:opacity-70 transition-ara"
                    style={{ ...inter, color: "var(--ara-ink)", fontWeight: 400 }}
                    data-testid={testid ? `${testid}-option-${item.id}` : undefined}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const fmtAmount = (a) => {
  const abs = Math.abs(Number(a) || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return a > 0 ? `+$${abs}` : `−$${abs}`;
};
const fmtDay = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export function BankActivitySection({ onTransactionsLoaded }) {
  const { household } = useAuth();
  const { members } = useHomeOSState();
  const householdId = household?.household_id;

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [txns, setTxns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showConnectNote, setShowConnectNote] = useState(false);
  const [visible, setVisible] = useState(12);

  const load = useCallback(async () => {
    if (!householdId) { setLoading(false); return; }
    const parse = async (settled) => {
      if (settled.status !== "fulfilled") return null;
      try { return await parseResponse(settled.value); } catch { return null; }
    };
    const [status, txRes, catRes, accRes] = await Promise.allSettled([
      api.akahu.getStatus(),
      api.transactions.list(householdId, { limit: 60 }),
      api.budgetCategories.list(householdId),
      api.accounts.list(householdId),
    ]).then((results) => Promise.all(results.map(parse)));
    if (status) setConfigured(Boolean(status?.dev_mode?.configured));
    if (txRes) setTxns(txRes.transactions || []);
    if (catRes) setCategories(catRes.categories || []);
    if (accRes) setAccounts(Array.isArray(accRes) ? accRes : []);
    setLoading(false);
  }, [householdId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onTransactionsLoaded?.(txns); }, [txns, onTransactionsLoaded]);

  const accountName = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.account_id] = a.display_name || a.name || "Account"; });
    return map;
  }, [accounts]);

  const bankAccounts = useMemo(
    () => accounts.filter((a) => a.external_link_type === "akahu" || (a.connection_id || "").startsWith("akahu_")),
    [accounts]
  );

  const catById = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c.category_id] = c; });
    return map;
  }, [categories]);

  const dupNames = useMemo(() => {
    const seen = {};
    categories.forEach((c) => { seen[c.name] = (seen[c.name] || 0) + 1; });
    return new Set(Object.keys(seen).filter((n) => seen[n] > 1));
  }, [categories]);

  const catLabel = useCallback((c) => {
    if (!c) return null;
    if (dupNames.has(c.name) || c.parent_group === "Insurance") return `${c.parent_group} · ${c.name}`;
    return c.name;
  }, [dupNames]);

  const catGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    categories.forEach((c) => {
      if (!cur || cur.label !== c.parent_group) { cur = { label: c.parent_group, items: [] }; groups.push(cur); }
      cur.items.push({ id: c.category_id, name: c.name, cat: c });
    });
    return groups;
  }, [categories]);

  const forOptions = useMemo(() => ([{
    label: null,
    items: [
      { id: "household", name: "Whole household" },
      ...(members || []).map((m) => ({ id: String(m.id || m.name), name: m.name })),
      { id: "shared", name: "Shared" },
    ],
  }]), [members]);

  const uncategorized = useMemo(() => txns.filter((t) => !t.category_id).length, [txns]);

  const changeCategory = async (txn, item) => {
    const prev = { category_id: txn.category_id, category_name: txn.category_name };
    setTxns((ts) => ts.map((t) => t.transaction_id === txn.transaction_id
      ? { ...t, category_id: item.id, category_name: item.name } : t));
    try {
      const updated = await parseResponse(await api.transactions.update(householdId, txn.transaction_id, { category_id: item.id }));
      if (updated?.rule_learned) toast("ARA will remember this.");
      else toast.success(`${item.name} — noted.`);
    } catch {
      setTxns((ts) => ts.map((t) => t.transaction_id === txn.transaction_id ? { ...t, ...prev } : t));
      toast("That didn't save — try once more.");
    }
  };

  const changeAssign = async (txn, item) => {
    const prev = { assigned_to: txn.assigned_to, assigned_to_label: txn.assigned_to_label };
    setTxns((ts) => ts.map((t) => t.transaction_id === txn.transaction_id
      ? { ...t, assigned_to: item.id, assigned_to_label: item.name } : t));
    try {
      await parseResponse(await api.transactions.update(householdId, txn.transaction_id, { assigned_to: item.id, assigned_to_label: item.name }));
      toast.success(`For ${item.name}.`);
    } catch {
      setTxns((ts) => ts.map((t) => t.transaction_id === txn.transaction_id ? { ...t, ...prev } : t));
      toast("That didn't save — try once more.");
    }
  };

  const syncNow = async () => {
    if (!householdId || syncing) return;
    setSyncing(true);
    try {
      const res = await parseResponse(await api.akahu.devSync(householdId));
      const imported = res?.imported_count ?? 0;
      toast.success(imported > 0
        ? `${imported} new transaction${imported === 1 ? "" : "s"} arrived.`
        : "Everything is up to date.");
      await load();
    } catch {
      toast("Your bank didn't respond just now. ARA will try again later.");
    } finally {
      setSyncing(false);
    }
  };

  const sentence = txns.length === 0
    ? (configured ? "Connected and ready — bring in your first transactions." : "What's moving through the household.")
    : uncategorized > 0
      ? `${uncategorized} transaction${uncategorized === 1 ? "" : "s"} still waiting for a category.`
      : "What's moving through the household.";

  const shown = txns.slice(0, visible);

  return (
    <section id="financials-recent-activity" data-testid="financials-recent-activity" className="mt-14">
      {/* Section header — subject first, supporting copy quieter */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <h2
            className="text-[22px] sm:text-[24px] leading-[1.15] tracking-[-0.01em]"
            style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink)", fontWeight: 400 }}
          >
            Bank activity
          </h2>
          <p className="mt-1.5 italic text-[12.5px] leading-[1.5] max-w-[52ch]"
            style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-muted)", fontWeight: 400 }}
            data-testid="bank-activity-sentence"
          >
            {sentence}
          </p>
        </div>
        {(configured && (txns.length > 0 || bankAccounts.length > 0)) && (
          <button
            type="button"
            onClick={syncNow}
            disabled={syncing}
            className="link-ara text-[13px] transition-ara hover:opacity-70 min-h-[44px] sm:min-h-0"
            style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
            data-testid="bank-sync-btn"
          >
            {syncing ? "Checking with your bank…" : "Sync now →"}
          </button>
        )}
      </div>

      {/* Connected accounts — quiet strip */}
      {bankAccounts.length > 0 && (
        <p className="mt-4 text-[12.5px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }} data-testid="bank-connected-accounts">
          {bankAccounts.map((a, i) => (
            <span key={a.account_id}>
              {i > 0 && <span aria-hidden> · </span>}
              {a.display_name || a.name}
              {typeof a.balance === "number" && (
                <span className="tabular-nums" style={{ color: "var(--ara-ink-muted)" }}>
                  {" "}${Number(a.balance).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </span>
          ))}
        </p>
      )}

      {loading ? null : txns.length === 0 ? (
        <div className="mt-8" data-testid="bank-activity-empty">
          {configured ? (
            <>
              <p className="italic text-[15px] leading-[1.5]" style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-muted)" }}>
                Your bank is connected. Bring in your transactions and ARA will quietly organise them.
              </p>
              <div className="mt-5">
                <PillButton variant="solid" onClick={syncNow} testid="bank-first-sync-btn">
                  {syncing ? "Bringing them in…" : "Bring in transactions"}
                </PillButton>
              </div>
            </>
          ) : (
            <>
              <p className="italic text-[15px] leading-[1.5]" style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-muted)" }}>
                Your Study is ready when you are.
              </p>
              <div className="mt-5 flex items-center gap-6 flex-wrap">
                <PillButton variant="solid" onClick={() => setShowConnectNote((s) => !s)} testid="bank-connect-btn">
                  Connect your bank →
                </PillButton>
              </div>
              <p className="mt-4 text-[13px] max-w-[52ch]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
                Connecting your accounts lets ARA quietly keep your household picture up to date.
              </p>
              {showConnectNote && (
                <div
                  className="mt-5 rounded-[12px] px-5 py-4 max-w-[560px] flex items-start gap-3"
                  style={{ border: "1px solid var(--ara-line)" }}
                  data-testid="bank-connect-note"
                >
                  <Landmark className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--ara-mod-money)" }} />
                  <p className="text-[13px] leading-[1.6]" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 400 }}>
                    ARA connects to New Zealand banks through Akahu. Your household's bank connection
                    details haven't been added yet — once they are, your accounts and transactions
                    will appear here automatically. Nothing else is needed from you.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div
            className="mt-6 rounded-[16px] overflow-visible"
            style={{ border: "1px solid var(--ara-line)" }}
            data-testid="bank-activity-table"
          >
            {shown.map((t, i) => {
              const cat = catById[t.category_id];
              const label = cat ? catLabel(cat) : (t.category_name || null);
              const isLast = i === shown.length - 1;
              const expanded = expandedId === t.transaction_id;
              const acct = accountName[t.account_id] || "Account";
              return (
                <div
                  key={t.transaction_id}
                  style={{ borderBottom: isLast ? "none" : "1px solid var(--ara-line-soft)" }}
                  data-testid={`bank-txn-row-${t.transaction_id}`}
                >
                  <div
                    className="px-5 sm:px-6 py-4 cursor-pointer"
                    onClick={() => setExpandedId(expanded ? null : t.transaction_id)}
                  >
                    {/* Desktop */}
                    <div className="hidden sm:grid grid-cols-[52px_1.5fr_auto_auto_120px] gap-4 items-center">
                      <p className="text-[12.5px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>{fmtDay(t.date)}</p>
                      <div className="min-w-0">
                        <p className="text-[14px] leading-tight truncate" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 500 }}>
                          {t.merchant || t.description}
                        </p>
                        <p className="mt-0.5 text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>{acct}</p>
                      </div>
                      <TagMenu
                        label={label}
                        placeholder="Choose category"
                        groups={catGroups}
                        onSelect={(item) => changeCategory(t, item)}
                        testid={`txn-category-tag-${t.transaction_id}`}
                      />
                      <TagMenu
                        label={t.assigned_to_label || null}
                        placeholder="For"
                        groups={forOptions}
                        onSelect={(item) => changeAssign(t, item)}
                        testid={`txn-assign-tag-${t.transaction_id}`}
                      />
                      <p className="text-[14px] tabular-nums text-right" style={{ ...inter, color: t.amount > 0 ? "var(--ara-mod-money)" : "var(--ara-ink)", fontWeight: 500 }}>
                        {fmtAmount(t.amount)}
                      </p>
                    </div>
                    {/* Mobile */}
                    <div className="sm:hidden">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-[14px] leading-tight truncate" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 500 }}>
                          {t.merchant || t.description}
                        </p>
                        <p className="text-[14px] tabular-nums whitespace-nowrap" style={{ ...inter, color: t.amount > 0 ? "var(--ara-mod-money)" : "var(--ara-ink)", fontWeight: 500 }}>
                          {fmtAmount(t.amount)}
                        </p>
                      </div>
                      <p className="mt-1 text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
                        {fmtDay(t.date)} · {acct}
                      </p>
                      <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                        <TagMenu
                          label={label}
                          placeholder="Choose category"
                          groups={catGroups}
                          onSelect={(item) => changeCategory(t, item)}
                          testid={`txn-category-tag-m-${t.transaction_id}`}
                        />
                        <TagMenu
                          label={t.assigned_to_label || null}
                          placeholder="For"
                          groups={forOptions}
                          onSelect={(item) => changeAssign(t, item)}
                          testid={`txn-assign-tag-m-${t.transaction_id}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Progressive disclosure */}
                  {expanded && (
                    <div
                      className="px-5 sm:px-6 pb-5 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2"
                      data-testid={`bank-txn-detail-${t.transaction_id}`}
                    >
                      {[
                        ["Description", t.description],
                        ["Date", new Date(t.date).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" })],
                        ["Account", acct],
                        ["Amount", fmtAmount(t.amount)],
                        ["Category", label || "Not yet chosen"],
                        ["For", t.assigned_to_label || "Whole household"],
                        ["Reference", [t.meta?.reference, t.meta?.particulars, t.meta?.code].filter(Boolean).join(" · ") || null],
                        ["Notes", t.notes || null],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <p key={k} className="text-[12.5px] leading-[1.6]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400 }}>
                          <span style={{ color: "var(--ara-ink-soft)" }}>{k} · </span>{v}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {txns.length > visible && (
            <div className="mt-4">
              <TextLink onClick={() => setVisible((v) => v + 15)} testid="bank-show-more">
                Show more →
              </TextLink>
            </div>
          )}

          {uncategorized > 0 && (
            <p
              className="mt-4 italic text-[13px]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-soft)" }}
              data-testid="bank-uncategorized-whisper"
            >
              {uncategorized} transaction{uncategorized === 1 ? "" : "s"} still waiting for a category. There's no rush.
            </p>
          )}
        </>
      )}
    </section>
  );
}
